// Transcript search results at /o/:slug/search (F2 / FR-L3a): GET /orgs/{id}/search. Titles and
// notes aren't searched — every hit comes from a transcript segment, with its own start_ms, so
// clicking one seeks the watch page straight to it (/g/:id?t=<seconds>).
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Avatar, Button, Callout } from "@grackle/ui";
import { ApiFail } from "../api/client";
import type { SearchHitOut, SearchResultOut } from "../api/types";
import { AppShell } from "../components/AppShell";
import { PageLoader, EmptyState } from "../components/ui";
import { useSession } from "../session";
import { searchGrackles } from "./api";
import { formatDuration, formatRelativeTime } from "./format";
import "./library.css";

const PAGE_SIZE = 20;

/** search.rs HTML-escapes snippets (`&amp; &lt; &gt; &quot; &#39;`) before turning its sentinels
 * into `<mark>`. Undo exactly those five escapes on the text parts, then render them as React text
 * nodes, so "a &lt;b&gt;" shows as "a <b>" without ever being parsed as markup. */
function unescapeSnippetText(text: string): string {
  return text.replace(/&(amp|lt|gt|quot|#39);/g, (_m, e: string) =>
    e === "amp" ? "&" : e === "lt" ? "<" : e === "gt" ? ">" : e === "quot" ? '"' : "'",
  );
}

/** Renders a `search.rs` snippet as text, with `<mark>` (the only markup it can contain) as a
 * real `<mark>` element — never `dangerouslySetInnerHTML`, so a transcript that said "<script>"
 * is shown literally instead of being reparsed as markup. */
export function renderSnippet(snippet: string): ReactNode {
  const parts = snippet.split(/(<mark>|<\/mark>)/);
  let marking = false;
  return parts.map((part, i) => {
    if (part === "<mark>") {
      marking = true;
      return null;
    }
    if (part === "</mark>") {
      marking = false;
      return null;
    }
    if (!part) return null;
    const text = unescapeSnippetText(part);
    return marking ? <mark key={i}>{text}</mark> : <span key={i}>{text}</span>;
  });
}

function HitRow({ grackleId, hit }: { grackleId: string; hit: SearchHitOut }) {
  const seconds = Math.floor(hit.start_ms / 1000);
  return (
    <div className="search-hit">
      <Link className="search-hit__time mono" to={`/g/${grackleId}?t=${seconds}`}>
        {formatDuration(hit.start_ms)}
      </Link>
      <span className="search-hit__snippet">{renderSnippet(hit.snippet)}</span>
    </div>
  );
}

function ResultRow({ result }: { result: SearchResultOut }) {
  return (
    <div className="search-result">
      <Link to={`/g/${result.grackle_id}`} className="search-result__title">
        {result.title || "Untitled"}
      </Link>
      <div className="search-result__meta">
        <Avatar name={result.owner.name} size={18} speaker={1} /> {result.owner.name} · {formatRelativeTime(result.created_at)}
      </div>
      <div className="search-result__hits">
        {result.hits.map((hit, i) => (
          <HitRow key={i} grackleId={result.grackle_id} hit={hit} />
        ))}
      </div>
    </div>
  );
}

export default function SearchPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") ?? "").trim();
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = orgs.find((o) => o.slug === slug);

  const [results, setResults] = useState<SearchResultOut[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (orgId: string, query: string) => {
    setLoading(true);
    setError(null);
    try {
      const page = await searchGrackles(orgId, { q: query, limit: PAGE_SIZE, offset: 0 });
      setResults(page);
      setHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't search your transcripts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!org || !q) {
      setResults([]);
      setHasMore(false);
      setLoading(false);
      return;
    }
    void load(org.id, q);
  }, [org?.id, q, load]);

  async function loadMore() {
    if (!org || !q || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await searchGrackles(org.id, { q, limit: PAGE_SIZE, offset: results.length });
      setResults((prev) => [...prev, ...page]);
      setHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load more results.");
    } finally {
      setLoadingMore(false);
    }
  }

  const canRecord = user?.email_verified ?? false;

  if (sessionLoading) return <PageLoader label="Searching…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;

  return (
    <AppShell org={org} user={user} section="search" canRecord={canRecord}>
      <div className="lib-header">
        <div className="lib-header-copy">
          <h1 className="lib-header-title">{q ? `Transcripts matching "${q}"` : "Search transcripts"}</h1>
          <div className="lib-header-sub">
            {q && !loading && !error ? `${results.length} recording${results.length === 1 ? "" : "s"} with matches · ` : ""}
            Searches what people said, not titles or notes.
          </div>
        </div>
      </div>

      {error ? (
        <div className="lib-error">
          <Callout tone="danger" icon="warning-circle">
            {error}
          </Callout>
        </div>
      ) : null}

      {!q ? null : error ? null : loading ? (
        <PageLoader label="Searching…" />
      ) : results.length === 0 ? (
        <EmptyState
          className="lib-empty"
          icon="magnifying-glass"
          title="No matches"
          body="Try a different phrase — transcript search looks at what was said, not the title."
        />
      ) : (
        <>
          <div className="search-results">
            {results.map((r) => (
              <ResultRow key={r.grackle_id} result={r} />
            ))}
          </div>
          {hasMore ? (
            <div className="lib-load-more">
              <Button variant="secondary" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </AppShell>
  );
}
